/**
 * CONTACT-MGMT (CM-6) — add / edit contact form.
 *
 * Enforces the required postal letter-header block (CM-D2/D6): companyName,
 * street, postCode, city, countryCode are starred + validated and block
 * submit. The rest are optional. `contactType` is not shown — the server
 * defaults `company` (CM-D3). The country control resolves to alpha-3
 * (CM-D17) via `ContactCountryAutocomplete`.
 *
 * Add mode additionally runs the dedupe-warn (CM-D8): as the user types the
 * company name, a debounced `GET /contacts?q=<name>` checks for an existing
 * ACTIVE contact and, if found, surfaces a non-blocking warning + a
 * "Use existing" link to that contact. Force-create stays allowed.
 *
 * Renders the form body + actions only (no Dialog/Page chrome) so it is
 * reusable from the list "Add" dialog and the detail "Edit" dialog, and
 * unit-testable in isolation.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ContactCountryAutocomplete } from "./ContactCountryAutocomplete.js";
import {
  useCreateContactMutation,
  useContactDedupeQuery,
  useUpdateContactMutation,
} from "./useContacts.js";
import type { ContactDetail, ContactWriteBody } from "./types.js";

export interface ContactFormProps {
  mode: "create" | "edit";
  /** Existing row to seed the form (edit mode). */
  initial?: ContactDetail;
  onCancel: () => void;
  /** Fired with the server row after a successful create/update. */
  onSaved: (contact: ContactDetail) => void;
}

interface FormState {
  companyName: string;
  street: string;
  postCode: string;
  city: string;
  countryCode: string | null;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  vatId: string;
  taxNumber: string;
  notes: string;
}

const EMPTY: FormState = {
  companyName: "",
  street: "",
  postCode: "",
  city: "",
  countryCode: null,
  contactPersonName: "",
  contactEmail: "",
  contactPhone: "",
  website: "",
  vatId: "",
  taxNumber: "",
  notes: "",
};

function seedFromDetail(d: ContactDetail): FormState {
  return {
    companyName: d.companyName ?? "",
    street: d.street ?? "",
    postCode: d.postCode ?? "",
    city: d.city ?? "",
    countryCode: d.countryCode,
    contactPersonName: d.contactPersonName ?? "",
    contactEmail: d.contactEmail ?? "",
    contactPhone: d.contactPhone ?? "",
    website: d.website ?? "",
    vatId: d.vatId ?? "",
    taxNumber: d.taxNumber ?? "",
    notes: d.notes ?? "",
  };
}

type RequiredKey = "companyName" | "street" | "postCode" | "city";

function buildBody(state: FormState): ContactWriteBody {
  const body: ContactWriteBody = {
    companyName: state.companyName.trim(),
    street: state.street.trim(),
    postCode: state.postCode.trim(),
    city: state.city.trim(),
    countryCode: state.countryCode ?? "",
  };
  const optional: [keyof ContactWriteBody, string][] = [
    ["contactPersonName", state.contactPersonName],
    ["contactEmail", state.contactEmail],
    ["contactPhone", state.contactPhone],
    ["website", state.website],
    ["vatId", state.vatId],
    ["taxNumber", state.taxNumber],
    ["notes", state.notes],
  ];
  for (const [key, raw] of optional) {
    const trimmed = raw.trim();
    if (trimmed.length > 0) {
      // Each optional key is a `string` in ContactWriteBody.
      body[key] = trimmed;
    }
  }
  return body;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function ContactForm({
  mode,
  initial,
  onCancel,
  onSaved,
}: ContactFormProps): ReactElement {
  const navigate = useNavigate();
  const [state, setState] = useState<FormState>(
    initial ? seedFromDetail(initial) : EMPTY,
  );
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createMutation = useCreateContactMutation();
  const editId = initial?.contactId ?? "";
  const updateMutation = useUpdateContactMutation(editId);
  const pending = createMutation.isPending || updateMutation.isPending;

  // Dedupe-warn (CM-D8) — Add mode only.
  const debouncedName = useDebounced(state.companyName, 400);
  const dedupe = useContactDedupeQuery(mode === "create" ? debouncedName : "");
  const duplicate = useMemo(() => {
    if (mode !== "create") return null;
    const typed = debouncedName.trim().toLowerCase();
    if (typed.length < 2) return null;
    return (
      dedupe.data?.items.find(
        (c) => !c.isDeleted && (c.companyName ?? "").toLowerCase() === typed,
      ) ??
      // Fall back to the first active near-match the BE returned (trigram).
      dedupe.data?.items.find((c) => !c.isDeleted) ??
      null
    );
  }, [mode, debouncedName, dedupe.data]);

  const requiredErrors = useMemo(() => {
    const errs: Partial<Record<RequiredKey | "countryCode", string>> = {};
    const req: RequiredKey[] = ["companyName", "street", "postCode", "city"];
    for (const key of req) {
      if (state[key].trim().length === 0) errs[key] = "Required";
    }
    if (!state.countryCode) errs.countryCode = "Required";
    return errs;
  }, [state]);

  const hasErrors = Object.keys(requiredErrors).length > 0;

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(): Promise<void> {
    setSubmitError(null);
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    const body = buildBody(state);
    try {
      const saved =
        mode === "create"
          ? await createMutation.mutateAsync(body)
          : await updateMutation.mutateAsync(body);
      onSaved(saved);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setSubmitError(
        status === 422
          ? "Some required fields are missing or invalid."
          : "Couldn't save the contact. Please try again.",
      );
    }
  }

  function err(key: RequiredKey): string | null {
    return showErrors ? (requiredErrors[key] ?? null) : null;
  }

  return (
    <Box
      component="form"
      noValidate
      aria-label={mode === "create" ? "Add contact" : "Edit contact"}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        {/* Dedupe-warn (CM-D8) — non-blocking. */}
        {duplicate ? (
          <Alert severity="warning">
            A contact named “{duplicate.companyName ?? "—"}” already exists.{" "}
            <MuiLink
              component="button"
              type="button"
              onClick={() => {
                void navigate(`/admin/contacts/${duplicate.contactId}`);
              }}
            >
              Use existing
            </MuiLink>{" "}
            — or continue to create a new one.
          </Alert>
        ) : null}

        <TextField
          label="Company name"
          required
          value={state.companyName}
          onChange={(e) => {
            set("companyName", e.target.value);
          }}
          error={err("companyName") !== null}
          helperText={err("companyName") ?? undefined}
          size="small"
          fullWidth
        />
        <TextField
          label="Street"
          required
          value={state.street}
          onChange={(e) => {
            set("street", e.target.value);
          }}
          error={err("street") !== null}
          helperText={err("street") ?? undefined}
          size="small"
          fullWidth
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Post code"
            required
            value={state.postCode}
            onChange={(e) => {
              set("postCode", e.target.value);
            }}
            error={err("postCode") !== null}
            helperText={err("postCode") ?? undefined}
            size="small"
            sx={{ width: { xs: "100%", sm: 200 } }}
          />
          <TextField
            label="City"
            required
            value={state.city}
            onChange={(e) => {
              set("city", e.target.value);
            }}
            error={err("city") !== null}
            helperText={err("city") ?? undefined}
            size="small"
            fullWidth
          />
        </Stack>
        <ContactCountryAutocomplete
          value={state.countryCode}
          onChange={(next) => {
            set("countryCode", next);
          }}
          required
          errorText={showErrors ? (requiredErrors.countryCode ?? null) : null}
        />

        <Box sx={{ height: 0 }} aria-hidden />

        <TextField
          label="Contact person"
          value={state.contactPersonName}
          onChange={(e) => {
            set("contactPersonName", e.target.value);
          }}
          size="small"
          fullWidth
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Email"
            type="email"
            value={state.contactEmail}
            onChange={(e) => {
              set("contactEmail", e.target.value);
            }}
            size="small"
            fullWidth
          />
          <TextField
            label="Phone"
            value={state.contactPhone}
            onChange={(e) => {
              set("contactPhone", e.target.value);
            }}
            size="small"
            fullWidth
          />
        </Stack>
        <TextField
          label="Website"
          value={state.website}
          onChange={(e) => {
            set("website", e.target.value);
          }}
          size="small"
          fullWidth
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="VAT ID"
            value={state.vatId}
            onChange={(e) => {
              set("vatId", e.target.value);
            }}
            size="small"
            fullWidth
          />
          <TextField
            label="Tax number"
            value={state.taxNumber}
            onChange={(e) => {
              set("taxNumber", e.target.value);
            }}
            size="small"
            fullWidth
          />
        </Stack>
        <TextField
          label="Notes"
          value={state.notes}
          onChange={(e) => {
            set("notes", e.target.value);
          }}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={pending}
            startIcon={
              pending ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            {mode === "create" ? "Create contact" : "Save changes"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
